// Registers default mutationFns for the Lists (household-items) offline write
// queue, keyed by the same mutationKey each hook in ./hooks.ts uses.
//
// Paused (offline-queued) mutations lose their closures across a reload — the
// persisted mutation cache (Task 1/2) only keeps the mutationKey + variables,
// not the mutationFn or the onMutate/onError/onSettled callbacks defined at
// the component call site. Registering a default mutationFn per mutationKey
// lets queryClient.resumePausedMutations() replay a queued mutation after a
// fresh page load, even though the hook that originally created it never ran.

import type { QueryClient } from '@tanstack/react-query';
import {
  createHouseholdItem,
  toggleHouseholdItem,
  clearCompletedHouseholdItems,
  restoreHouseholdItems,
  reorderHouseholdItems,
} from './api';
import type { CreateHouseholdItemPayload } from '@/types/api';

export function registerHouseholdItemMutationDefaults(qc: QueryClient) {
  qc.setMutationDefaults(['household-items', 'create'], {
    mutationFn: (data: CreateHouseholdItemPayload) => createHouseholdItem(data),
  });
  qc.setMutationDefaults(['household-items', 'toggle'], {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- thin resume-only wrapper; hooks.ts is the real, typed call site.
    mutationFn: (id: any) => toggleHouseholdItem(id),
  });
  qc.setMutationDefaults(['household-items', 'clear'], {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- thin resume-only wrapper; hooks.ts is the real, typed call site.
    mutationFn: (listId: any) => clearCompletedHouseholdItems(listId),
  });
  qc.setMutationDefaults(['household-items', 'restore'], {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- thin resume-only wrapper; hooks.ts is the real, typed call site.
    mutationFn: (itemIds: any) => restoreHouseholdItems(itemIds),
  });
  qc.setMutationDefaults(['household-items', 'reorder'], {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- thin resume-only wrapper; hooks.ts is the real, typed call site.
    mutationFn: (v: any) => reorderHouseholdItems(v.listId, v.itemIds),
  });
}
